import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Header from './Header';
import ReportsList from '../ReportsList';
import CustomersList from '../CustomersList';
import { useTranslation } from 'react-i18next';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/customAccordion.css';

const AsideNavItem = ({ to, icon, label, isActive }) => (
  <div className="accordion-item aside-nav-item">
    <Link
      className={`aside-nav-link ${isActive ? 'is-active' : ''}`}
      to={to}
      aria-current={isActive ? 'page' : undefined}
    >
      <i className={`bi ${icon}`} aria-hidden="true"></i>
      <span>{label}</span>
    </Link>
  </div>
);

const Aside = ({
  reports, 
  folders, 
  customers, 
  handleSelectCustomer,
  handleClearCustomerSelection,
  handleOpenCustomerModal, 
  handleSelectReport, 
  handleDeleteCustomer, 
  handleDeleteReport, 
  handleCreateNewReport, 
  handleUpdateReportName, 
  setSection, 
  clearSelections, 
  moveReportToFolder, 
  onRenameFolder,
  onSaveFolder,
  onCreateFolder,
  onDeleteFolder,
  role,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isCustomersOpen, setIsCustomersOpen] = useState(false);
  const isRouteActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleReportClick = (reportId, reportName) => {
    handleSelectReport(reportId, reportName);
    navigate(`/reports/${reportId}`);
    onClose?.();
  };

  const handleCustomerClick = (customer) => {
    handleSelectCustomer(customer);
    setSection(t('customers'));
    navigate('/customers');
    onClose?.();
  };

  const handleCustomersOverviewClick = () => {
    handleClearCustomerSelection?.();
    setSection(t('customers'));
  };

  const handleAsideClick = (event) => {
    if (event.target.closest('a')) onClose?.();
  };

  return (
    <aside className="aside-custom d-flex flex-column pt-1" onClick={handleAsideClick}>
      {onClose && (
        <button type="button" className="app-aside-close" onClick={onClose} aria-label={t('close_navigation')}>
          <i className="bi bi-x-lg" />
        </button>
      )}
      <Header setSection={setSection} clearSelections={clearSelections} />
      <div className="accordion flex-grow-1 aside-navigation" id="accordionAsideMenu">
        <nav className="aside-primary-nav" aria-label={t('main_navigation')}>
          {role === 'admin' && (
            <div className="aside-menu-group">
              <div className="group-label">{t('nav_admin')}</div>
              <AsideNavItem
                to="/admin"
                icon="bi-shield-lock"
                label={t('admin_title')}
                isActive={isRouteActive('/admin')}
              />
            </div>
          )}

          <div className="aside-menu-group">
            <div className="group-label">{t('nav_overview')}</div>
            <AsideNavItem
              to="/dashboard"
              icon="bi-house-door"
              label={t('dashboard')}
              isActive={isRouteActive('/dashboard')}
            />
            <AsideNavItem
              to="/calendar"
              icon="bi-calendar3"
              label={t('calendar_title')}
              isActive={isRouteActive('/calendar')}
            />
          </div>

          <div className="aside-menu-group">
            <div className="group-label">{t('nav_work')}</div>
            <AsideNavItem
              to="/projects"
              icon="bi-card-checklist"
              label={t('projects_title')}
              isActive={isRouteActive('/projects')}
            />

            <div className="accordion-item aside-customers-item">
              <div className={`aside-customers-row ${isRouteActive('/customers') ? 'is-active' : ''} ${isCustomersOpen ? 'is-open' : ''}`}>
                <Link
                  className="aside-customers-link"
                  to="/customers"
                  onClick={handleCustomersOverviewClick}
                  aria-current={isRouteActive('/customers') ? 'page' : undefined}
                >
                  <i className="bi bi-person" aria-hidden="true"></i>
                  <span>{t('customers')}</span>
                </Link>
                <button
                  className="aside-customers-toggle"
                  type="button"
                  onClick={() => setIsCustomersOpen((isOpen) => !isOpen)}
                  aria-expanded={isCustomersOpen}
                  aria-controls="asideCustomersList"
                  aria-label={t('toggle_customers')}
                  title={t('toggle_customers')}
                >
                  <i className="bi bi-chevron-down" aria-hidden="true"></i>
                </button>
              </div>
              <div id="asideCustomersList" className={`accordion-collapse collapse ${isCustomersOpen ? 'show' : ''}`}>
                <div className="accordion-body aside-customers-list">
                  <CustomersList
                    customers={customers}
                    onDeleteCustomer={handleDeleteCustomer}
                    onSelectCustomer={handleCustomerClick}
                    onOpenCustomerModal={handleOpenCustomerModal}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="aside-menu-group">
            <div className="group-label">{t('nav_documents')}</div>
            <AsideNavItem
              to="/reports"
              icon="bi-file-earmark-bar-graph"
              label={t('work_reports')}
              isActive={isRouteActive('/reports')}
            />
            <AsideNavItem
              to="/price-quotes"
              icon="bi-receipt"
              label={t('price_quote_title')}
              isActive={isRouteActive('/price-quotes')}
            />
            <AsideNavItem
              to="/contracts"
              icon="bi-file-earmark-text"
              label={t('contracts_title')}
              isActive={isRouteActive('/contracts')}
            />
          </div>

          <div className="aside-menu-group">
            <div className="group-label">{t('nav_finance_tools')}</div>
            <AsideNavItem
              to="/finance"
              icon="bi-graph-up"
              label={t('finance_title')}
              isActive={isRouteActive('/finance')}
            />
            <AsideNavItem
              to="/calculators-tools"
              icon="bi-calculator"
              label={t('calculators_tools')}
              isActive={isRouteActive('/calculators-tools')}
            />
          </div>

          <div className="aside-menu-group">
            <div className="group-label">{t('nav_opportunities')}</div>
            <AsideNavItem
              to="/jobseeker"
              icon="bi-search-heart"
              label={t('jobseeker_title')}
              isActive={isRouteActive('/jobseeker')}
            />
          </div>
        </nav>

        <section className="aside-menu-group aside-quick-access" aria-labelledby="asideQuickAccessTitle">
          <div className="group-label" id="asideQuickAccessTitle">{t('quick_access')}</div>
          <ReportsList
            reports={reports}
            folders={folders}
            onSelectReport={handleReportClick}
            onDeleteReport={handleDeleteReport}
            onCreateNewReport={handleCreateNewReport}
            onUpdateReportName={handleUpdateReportName}
            onMoveReportToFolder={moveReportToFolder}
            onRenameFolder={onRenameFolder}
            onSaveFolder={onSaveFolder}
            onCreateFolder={onCreateFolder}
            onDeleteFolder={onDeleteFolder}
          />
        </section>
      </div>
    </aside>
  );
};

export default Aside;
